import type {
  GeneratedArtifact,
  GeneratorArtifactKind,
  GeneratorArtifactWritePolicy,
  SkippedGeneratedArtifactReason,
  WrittenGeneratedArtifactReason,
} from './types.ts'

export interface ArtifactMergeResult {
  action: 'write' | 'unchanged' | 'skip'
  content?: string
  writeReason?: WrittenGeneratedArtifactReason
  skipReason?: SkippedGeneratedArtifactReason
  added: string[]
  preservedCustom: string[]
  conflicts: string[]
  lintErrors: string[]
  detail?: string
}

interface StyleFingerprint {
  quote: "'" | '"'
  semicolons: boolean
  trailingComma: boolean
  indent: string
  newline: '\n' | '\r\n'
}

interface NamedImport {
  names: string[]
  module: string
  raw: string
  start: number
  end: number
}

interface ObjectEntry {
  key: string
  value: string
  raw: string
}

interface DefineBlock {
  kind: 'database' | 'registry'
  exportName: string
  callName: string
  entries: ObjectEntry[]
  bodyStart: number
  bodyEnd: number
  fullStart: number
  fullEnd: number
  raw: string
}

interface MetaBlock {
  entries: ObjectEntry[]
  bodyStart: number
  bodyEnd: number
  fullStart: number
  fullEnd: number
  raw: string
}

const IMPORT_RE =
  /^import\s+(?:type\s+)?(?:\{([^}]*)\}|([A-Za-z_$][\w$]*))\s+from\s+(['"])([^'"]+)\3\s*;?\s*$/gm

const DEFINE_EXPORT_RE =
  /export\s+const\s+([A-Za-z_$][\w$]*)\s*=\s*(define(?:Database|Registry))\s*\(\s*\{/g

const META_EXPORT_RE =
  /export\s+const\s+__athena_schema_meta\s*=\s*\{/g

const KNOWN_META_KEYS = new Set([
  'schemaVersion',
  'generatedAt',
  'database',
  'outputPreset',
  'outputFormat',
])

function detectNewline(source: string): '\n' | '\r\n' {
  return source.includes('\r\n') ? '\r\n' : '\n'
}

function detectStyle(source: string): StyleFingerprint {
  const newline = detectNewline(source)
  const single = (source.match(/'/g) ?? []).length
  const double = (source.match(/"/g) ?? []).length
  const quote: "'" | '"' = double > single ? '"' : "'"

  const importLines = source.split(/\r?\n/).filter(line => line.trimStart().startsWith('import '))
  const semicolons =
    importLines.length > 0
      ? importLines.filter(line => line.trimEnd().endsWith(';')).length >= importLines.length / 2
      : false

  const objectLines = source
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => /^[A-Za-z0-9_'"]+\s*:\s*.+/.test(line))
  const trailingComma =
    objectLines.length > 0
      ? objectLines.filter(line => line.endsWith(',')).length >= Math.ceil(objectLines.length / 2)
      : true

  const indentMatch = source.match(/\n([ \t]+)\S/)
  const indent = indentMatch?.[1] ?? '  '

  return { quote, semicolons, trailingComma, indent, newline }
}

function quoteString(value: string, quote: "'" | '"'): string {
  const escaped = value
    .replace(/\\/g, '\\\\')
    .replace(new RegExp(quote, 'g'), `\\${quote}`)
  return `${quote}${escaped}${quote}`
}

function parseNamedImports(source: string): NamedImport[] {
  const imports: NamedImport[] = []
  IMPORT_RE.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = IMPORT_RE.exec(source)) !== null) {
    const named = match[1]
    const defaultName = match[2]
    const module = match[4]
    const names = named
      ? named
          .split(',')
          .map(part => part.trim())
          .filter(Boolean)
          .map(part => {
            const alias = part.split(/\s+as\s+/)
            return (alias[1] ?? alias[0]).trim()
          })
      : defaultName
        ? [defaultName]
        : []

    imports.push({
      names,
      module,
      raw: match[0],
      start: match.index,
      end: match.index + match[0].length,
    })
  }
  return imports
}

function findMatchingBrace(source: string, openIndex: number): number {
  let depth = 0
  let inSingle = false
  let inDouble = false
  let inTemplate = false
  let escaped = false

  for (let i = openIndex; i < source.length; i += 1) {
    const ch = source[i]
    if (escaped) {
      escaped = false
      continue
    }
    if (ch === '\\' && (inSingle || inDouble || inTemplate)) {
      escaped = true
      continue
    }
    if (!inDouble && !inTemplate && ch === "'") {
      inSingle = !inSingle
      continue
    }
    if (!inSingle && !inTemplate && ch === '"') {
      inDouble = !inDouble
      continue
    }
    if (!inSingle && !inDouble && ch === '`') {
      inTemplate = !inTemplate
      continue
    }
    if (inSingle || inDouble || inTemplate) {
      continue
    }
    if (ch === '{') {
      depth += 1
    } else if (ch === '}') {
      depth -= 1
      if (depth === 0) {
        return i
      }
    }
  }
  return -1
}

function parseObjectEntries(body: string): ObjectEntry[] {
  const entries: ObjectEntry[] = []
  const lines = body.split(/\r?\n/)
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('//') || trimmed.startsWith('/*')) {
      continue
    }
    const withoutComma = trimmed.endsWith(',') ? trimmed.slice(0, -1).trimEnd() : trimmed
    const match = withoutComma.match(/^([A-Za-z_$][\w$]*|['"][^'"]+['"])\s*:\s*(.+)$/)
    if (!match) {
      continue
    }
    const keyRaw = match[1]
    const key =
      keyRaw.startsWith("'") || keyRaw.startsWith('"')
        ? keyRaw.slice(1, -1)
        : keyRaw
    entries.push({
      key,
      value: match[2].trim(),
      raw: trimmed,
    })
  }
  return entries
}

function parseDefineBlocks(source: string): DefineBlock[] {
  const blocks: DefineBlock[] = []
  DEFINE_EXPORT_RE.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = DEFINE_EXPORT_RE.exec(source)) !== null) {
    const exportName = match[1]
    const callName = match[2]
    const openBrace = match.index + match[0].lastIndexOf('{')
    const closeBrace = findMatchingBrace(source, openBrace)
    if (closeBrace < 0) {
      continue
    }
    const body = source.slice(openBrace + 1, closeBrace)
    const fullEnd = (() => {
      let i = closeBrace + 1
      while (i < source.length && /\s/.test(source[i])) i += 1
      if (source[i] === ')') i += 1
      while (i < source.length && /\s/.test(source[i])) i += 1
      if (source[i] === ';') i += 1
      return i
    })()
    blocks.push({
      kind: callName === 'defineDatabase' ? 'database' : 'registry',
      exportName,
      callName,
      entries: parseObjectEntries(body),
      bodyStart: openBrace + 1,
      bodyEnd: closeBrace,
      fullStart: match.index,
      fullEnd,
      raw: source.slice(match.index, fullEnd),
    })
  }
  return blocks
}

function parseMetaBlock(source: string): MetaBlock | undefined {
  META_EXPORT_RE.lastIndex = 0
  const match = META_EXPORT_RE.exec(source)
  if (!match) {
    return undefined
  }
  const openBrace = match.index + match[0].lastIndexOf('{')
  const closeBrace = findMatchingBrace(source, openBrace)
  if (closeBrace < 0) {
    return undefined
  }
  let fullEnd = closeBrace + 1
  const after = source.slice(fullEnd)
  const asConst = after.match(/^\s*as\s+const\s*;?/)
  if (asConst) {
    fullEnd += asConst[0].length
  } else if (source[fullEnd] === ';') {
    fullEnd += 1
  }
  return {
    entries: parseObjectEntries(source.slice(openBrace + 1, closeBrace)),
    bodyStart: openBrace + 1,
    bodyEnd: closeBrace,
    fullStart: match.index,
    fullEnd,
    raw: source.slice(match.index, fullEnd),
  }
}

function hasImportBinding(imports: NamedImport[], name: string, module?: string): boolean {
  return imports.some(
    item =>
      item.names.includes(name) &&
      (module === undefined || normalizeModulePath(item.module) === normalizeModulePath(module)),
  )
}

function findImportForName(imports: NamedImport[], name: string): NamedImport | undefined {
  return imports.find(item => item.names.includes(name))
}

function normalizeModulePath(modulePath: string): string {
  return modulePath.replace(/\\/g, '/').replace(/^\.\//, '').replace(/\.ts$/, '')
}

function formatImport(names: string[], modulePath: string, style: StyleFingerprint): string {
  const body = `import { ${names.join(', ')} } from ${quoteString(modulePath, style.quote)}`
  return style.semicolons ? `${body};` : body
}

function formatObjectEntry(key: string, value: string, style: StyleFingerprint, isLast: boolean): string {
  const needsQuote = !/^[A-Za-z_$][\w$]*$/.test(key)
  const renderedKey = needsQuote ? quoteString(key, style.quote) : key
  const comma = !isLast || style.trailingComma ? ',' : ''
  return `${style.indent}${renderedKey}: ${value}${comma}`
}

function replaceRange(source: string, start: number, end: number, insertion: string): string {
  return source.slice(0, start) + insertion + source.slice(end)
}

function collectDuplicateKeys(entries: ObjectEntry[]): string[] {
  const seen = new Set<string>()
  const dupes: string[] = []
  for (const entry of entries) {
    if (seen.has(entry.key)) {
      dupes.push(entry.key)
    }
    seen.add(entry.key)
  }
  return dupes
}

function collectDuplicateImportBindings(imports: NamedImport[]): string[] {
  const seen = new Set<string>()
  const dupes: string[] = []
  for (const item of imports) {
    for (const name of item.names) {
      if (seen.has(name)) {
        dupes.push(name)
      }
      seen.add(name)
    }
  }
  return dupes
}

function lintArtifactSource(
  source: string,
  kind: 'database' | 'registry',
): string[] {
  const errors: string[] = []
  const imports = parseNamedImports(source)
  const dupImports = collectDuplicateImportBindings(imports)
  if (dupImports.length > 0) {
    errors.push(`duplicate import bindings: ${dupImports.join(', ')}`)
  }

  const blocks = parseDefineBlocks(source).filter(block => block.kind === kind)
  if (blocks.length === 0) {
    errors.push(`missing export const … = define${kind === 'database' ? 'Database' : 'Registry'}({…})`)
    return errors
  }
  if (blocks.length > 1) {
    errors.push(`multiple define${kind === 'database' ? 'Database' : 'Registry'} exports found`)
  }

  const block = blocks[0]
  const dupKeys = collectDuplicateKeys(block.entries)
  if (dupKeys.length > 0) {
    errors.push(`duplicate object keys: ${dupKeys.join(', ')}`)
  }

  for (const entry of block.entries) {
    const valueId = entry.value.match(/^[A-Za-z_$][\w$]*$/)?.[0]
    if (valueId && !hasImportBinding(imports, valueId) && valueId !== block.exportName) {
      // value should be imported (schema const / database const)
      if (!source.includes(`const ${valueId}`) && !source.includes(`function ${valueId}`)) {
        errors.push(`value "${valueId}" for key "${entry.key}" is not imported`)
      }
    }
  }

  if (kind === 'database' && !hasImportBinding(imports, 'defineDatabase')) {
    errors.push('missing defineDatabase import')
  }
  if (kind === 'registry' && !hasImportBinding(imports, 'defineRegistry')) {
    errors.push('missing defineRegistry import')
  }

  return errors
}

function preservedCustomUnits(
  existing: string,
  generated: string,
  kind: 'database' | 'registry',
): string[] {
  const custom: string[] = []
  const existingImports = parseNamedImports(existing)
  const generatedImports = parseNamedImports(generated)
  const generatedModules = new Set(generatedImports.map(item => normalizeModulePath(item.module)))
  const generatedNames = new Set(generatedImports.flatMap(item => item.names))

  for (const item of existingImports) {
    const moduleNorm = normalizeModulePath(item.module)
    if (moduleNorm.includes('@xylex-group/athena')) {
      continue
    }
    const unexpectedNames = item.names.filter(name => !generatedNames.has(name))
    if (unexpectedNames.length > 0 && !generatedModules.has(moduleNorm)) {
      custom.push(`import { ${unexpectedNames.join(', ')} } from '${item.module}'`)
    } else if (unexpectedNames.length > 0) {
      custom.push(`import binding(s): ${unexpectedNames.join(', ')}`)
    }
  }

  const existingBlocks = parseDefineBlocks(existing).filter(block => block.kind === kind)
  const generatedBlocks = parseDefineBlocks(generated).filter(block => block.kind === kind)
  const generatedKeys = new Set(generatedBlocks[0]?.entries.map(entry => entry.key) ?? [])
  for (const entry of existingBlocks[0]?.entries ?? []) {
    if (!generatedKeys.has(entry.key)) {
      custom.push(`${kind} entry: ${entry.key}: ${entry.value}`)
    }
  }

  if (kind === 'registry') {
    const existingMeta = parseMetaBlock(existing)
    const generatedMeta = parseMetaBlock(generated)
    const generatedMetaKeys = new Set(generatedMeta?.entries.map(entry => entry.key) ?? [])
    for (const entry of existingMeta?.entries ?? []) {
      if (!generatedMetaKeys.has(entry.key) && !KNOWN_META_KEYS.has(entry.key)) {
        custom.push(`meta entry: ${entry.key}: ${entry.value}`)
      }
    }
  }

  // Extra top-level export const lines not in generated output
  const exportConstRe = /^export\s+const\s+([A-Za-z_$][\w$]*)\b/gm
  const generatedExports = new Set<string>()
  let match: RegExpExecArray | null
  exportConstRe.lastIndex = 0
  while ((match = exportConstRe.exec(generated)) !== null) {
    generatedExports.add(match[1])
  }
  exportConstRe.lastIndex = 0
  while ((match = exportConstRe.exec(existing)) !== null) {
    if (!generatedExports.has(match[1]) && match[1] !== '__athena_schema_meta') {
      custom.push(`export const ${match[1]}`)
    }
  }

  return custom
}

function insertImportAfterPackageImports(
  source: string,
  importLine: string,
  style: StyleFingerprint,
): string {
  const imports = parseNamedImports(source)
  if (imports.length === 0) {
    return `${importLine}${style.newline}${source}`
  }

  // Prefer inserting after the last local/relative import; if none, after last import.
  let anchor = imports[imports.length - 1]
  for (let i = imports.length - 1; i >= 0; i -= 1) {
    if (imports[i].module.startsWith('.')) {
      anchor = imports[i]
      break
    }
  }

  const insertAt = anchor.end
  const before = source.slice(0, insertAt)
  const after = source.slice(insertAt)
  const needsLeadingNl = !before.endsWith('\n')
  const prefix = needsLeadingNl ? style.newline : ''
  return `${before}${prefix}${importLine}${after.startsWith('\n') || after.startsWith('\r\n') ? '' : style.newline}${after}`
}

function rewriteObjectBody(
  entries: ObjectEntry[],
  style: StyleFingerprint,
): string {
  if (entries.length === 0) {
    return style.newline
  }
  const lines = entries.map((entry, index) =>
    formatObjectEntry(entry.key, entry.value, style, index === entries.length - 1),
  )
  return `${style.newline}${lines.join(style.newline)}${style.newline}`
}

function mergeDatabaseArtifact(existing: string, generated: string): ArtifactMergeResult {
  const style = detectStyle(existing)
  const generatedStyle = detectStyle(generated)
  const effectiveStyle: StyleFingerprint = {
    ...style,
    // Prefer existing fingerprint; fall back to generated if existing is empty-ish
    quote: existing.includes('"') || existing.includes("'") ? style.quote : generatedStyle.quote,
  }

  const generatedImports = parseNamedImports(generated)
  const existingBlocks = parseDefineBlocks(existing).filter(block => block.kind === 'database')
  const generatedBlocks = parseDefineBlocks(generated).filter(block => block.kind === 'database')

  if (existingBlocks.length === 0 || generatedBlocks.length === 0) {
    return {
      action: 'skip',
      skipReason: 'merge-unparseable',
      added: [],
      preservedCustom: [],
      conflicts: [],
      lintErrors: [],
      detail: 'could not locate defineDatabase({…}) export for merge',
    }
  }

  const generatedBlock = generatedBlocks[0]
  const conflicts: string[] = []
  const added: string[] = []
  let next = existing

  // Ensure defineDatabase import
  if (!hasImportBinding(parseNamedImports(next), 'defineDatabase')) {
    const pkgImport = generatedImports.find(item => item.names.includes('defineDatabase'))
    if (pkgImport) {
      const line = formatImport(['defineDatabase'], pkgImport.module, effectiveStyle)
      next = insertImportAfterPackageImports(next, line, effectiveStyle)
      added.push('import defineDatabase')
    }
  }

  // Re-parse after possible import insert
  let workingImports = parseNamedImports(next)
  let workingBlocks = parseDefineBlocks(next).filter(block => block.kind === 'database')
  let workingBlock = workingBlocks[0]
  const entryMap = new Map(workingBlock.entries.map(entry => [entry.key, entry]))

  for (const desired of generatedBlock.entries) {
    const existingEntry = entryMap.get(desired.key)
    if (existingEntry) {
      if (existingEntry.value !== desired.value) {
        conflicts.push(
          `key "${desired.key}" maps to ${existingEntry.value} (existing) vs ${desired.value} (generated)`,
        )
      }
      continue
    }

    // Find generated import for the schema const
    const desiredImport = generatedImports.find(item => item.names.includes(desired.value))
    if (desiredImport) {
      const existingForName = findImportForName(workingImports, desired.value)
      if (existingForName) {
        if (
          normalizeModulePath(existingForName.module) !== normalizeModulePath(desiredImport.module)
        ) {
          conflicts.push(
            `binding "${desired.value}" imported from '${existingForName.module}' vs '${desiredImport.module}'`,
          )
          continue
        }
      } else {
        const line = formatImport([desired.value], desiredImport.module, effectiveStyle)
        next = insertImportAfterPackageImports(next, line, effectiveStyle)
        added.push(`import ${desired.value}`)
        workingImports = parseNamedImports(next)
      }
    }

    // Re-locate block after import edits
    workingBlocks = parseDefineBlocks(next).filter(block => block.kind === 'database')
    workingBlock = workingBlocks[0]
    const nextEntries = [...workingBlock.entries, { key: desired.key, value: desired.value, raw: '' }]
    const body = rewriteObjectBody(nextEntries, effectiveStyle)
    next = replaceRange(next, workingBlock.bodyStart, workingBlock.bodyEnd, body)
    entryMap.set(desired.key, { key: desired.key, value: desired.value, raw: '' })
    added.push(`database entry: ${desired.key}`)
  }

  if (conflicts.length > 0 && added.length === 0) {
    return {
      action: 'skip',
      skipReason: 'merge-conflict',
      added: [],
      preservedCustom: preservedCustomUnits(existing, generated, 'database'),
      conflicts,
      lintErrors: [],
      detail: conflicts.join('; '),
    }
  }

  const lintErrors = lintArtifactSource(next, 'database')
  if (lintErrors.length > 0) {
    return {
      action: 'skip',
      skipReason: 'merge-lint-failed',
      added,
      preservedCustom: preservedCustomUnits(existing, generated, 'database'),
      conflicts,
      lintErrors,
      detail: lintErrors.join('; '),
    }
  }

  const preservedCustom = preservedCustomUnits(next, generated, 'database')
  if (next === existing || next.replace(/\r\n/g, '\n') === existing.replace(/\r\n/g, '\n')) {
    return {
      action: 'unchanged',
      content: existing,
      skipReason: 'already-current',
      added: [],
      preservedCustom,
      conflicts,
      lintErrors: [],
      detail: conflicts.length > 0 ? conflicts.join('; ') : undefined,
    }
  }

  return {
    action: 'write',
    content: next.endsWith('\n') ? next : `${next}${effectiveStyle.newline}`,
    writeReason: 'merged',
    added,
    preservedCustom,
    conflicts,
    lintErrors: [],
  }
}

function mergeMetaFields(
  existingMeta: MetaBlock | undefined,
  generatedMeta: MetaBlock | undefined,
): { entries: ObjectEntry[]; changed: boolean; added: string[] } {
  const added: string[] = []
  if (!generatedMeta) {
    return { entries: existingMeta?.entries ?? [], changed: false, added }
  }
  if (!existingMeta) {
    return {
      entries: generatedMeta.entries,
      changed: true,
      added: generatedMeta.entries.map(entry => `meta entry: ${entry.key}`),
    }
  }

  const map = new Map(existingMeta.entries.map(entry => [entry.key, entry]))
  let changed = false

  for (const desired of generatedMeta.entries) {
    const current = map.get(desired.key)
    if (!current) {
      map.set(desired.key, desired)
      added.push(`meta entry: ${desired.key}`)
      changed = true
      continue
    }
    // Refresh known meta fields when they differ (except generatedAt — only if other meta missing was handled).
    // Always refresh generatedAt / schemaVersion / output fields when values differ so registry stays truthful.
    if (KNOWN_META_KEYS.has(desired.key) && current.value !== desired.value) {
      map.set(desired.key, desired)
      added.push(`meta refresh: ${desired.key}`)
      changed = true
    }
  }

  // Preserve order: existing keys first, then newly added known keys in generated order
  const ordered: ObjectEntry[] = []
  const seen = new Set<string>()
  for (const entry of existingMeta.entries) {
    const next = map.get(entry.key)
    if (next) {
      ordered.push(next)
      seen.add(entry.key)
    }
  }
  for (const entry of generatedMeta.entries) {
    if (!seen.has(entry.key)) {
      const next = map.get(entry.key)
      if (next) {
        ordered.push(next)
        seen.add(entry.key)
      }
    }
  }
  for (const [key, entry] of map) {
    if (!seen.has(key)) {
      ordered.push(entry)
    }
  }

  return { entries: ordered, changed, added }
}

function mergeRegistryArtifact(existing: string, generated: string): ArtifactMergeResult {
  const style = detectStyle(existing)
  const effectiveStyle = style
  const generatedImports = parseNamedImports(generated)
  const existingBlocks = parseDefineBlocks(existing).filter(block => block.kind === 'registry')
  const generatedBlocks = parseDefineBlocks(generated).filter(block => block.kind === 'registry')

  if (existingBlocks.length === 0 || generatedBlocks.length === 0) {
    return {
      action: 'skip',
      skipReason: 'merge-unparseable',
      added: [],
      preservedCustom: [],
      conflicts: [],
      lintErrors: [],
      detail: 'could not locate defineRegistry({…}) export for merge',
    }
  }

  const existingBlock = existingBlocks[0]
  const generatedBlock = generatedBlocks[0]
  const conflicts: string[] = []
  const added: string[] = []
  let next = existing

  if (!hasImportBinding(parseNamedImports(next), 'defineRegistry')) {
    const pkgImport = generatedImports.find(item => item.names.includes('defineRegistry'))
    if (pkgImport) {
      next = insertImportAfterPackageImports(
        next,
        formatImport(['defineRegistry'], pkgImport.module, effectiveStyle),
        effectiveStyle,
      )
      added.push('import defineRegistry')
    }
  }

  // Database const import from generated (prefer existing const name if entry already present)
  const preferredDbValue =
    existingBlock.entries[0]?.value ?? generatedBlock.entries[0]?.value
  const generatedDbImport =
    generatedImports.find(item => item.names.includes(generatedBlock.entries[0]?.value ?? '')) ??
    generatedImports.find(item => item.module.startsWith('.'))

  if (preferredDbValue && generatedDbImport) {
    const existingForName = findImportForName(parseNamedImports(next), preferredDbValue)
    if (!existingForName) {
      const modulePath = generatedDbImport.module
      const importName =
        findImportForName(generatedImports, preferredDbValue)?.names[0] ??
        generatedBlock.entries[0]?.value ??
        preferredDbValue
      if (!hasImportBinding(parseNamedImports(next), importName)) {
        next = insertImportAfterPackageImports(
          next,
          formatImport([importName], modulePath, effectiveStyle),
          effectiveStyle,
        )
        added.push(`import ${importName}`)
      }
    } else if (
      normalizeModulePath(existingForName.module) !== normalizeModulePath(generatedDbImport.module)
    ) {
      conflicts.push(
        `binding "${preferredDbValue}" imported from '${existingForName.module}' vs '${generatedDbImport.module}'`,
      )
    }
  }

  // Merge defineRegistry entries (additive)
  let workingBlocks = parseDefineBlocks(next).filter(block => block.kind === 'registry')
  let workingBlock = workingBlocks[0]
  const entryMap = new Map(workingBlock.entries.map(entry => [entry.key, entry]))

  for (const desired of generatedBlock.entries) {
    // Prefer existing value name for same key if present
    const existingEntry = entryMap.get(desired.key)
    if (existingEntry) {
      // Keep existing value (const name); only conflict if we cannot reconcile
      continue
    }

    // Use desired key with existing imported database const if any
    const dbImport = parseNamedImports(next).find(
      item =>
        item.module.startsWith('.') &&
        item.names.some(name => name !== 'defineRegistry'),
    )
    const value = dbImport?.names[0] ?? desired.value
    const nextEntries = [...workingBlock.entries, { key: desired.key, value, raw: '' }]
    const body = rewriteObjectBody(nextEntries, effectiveStyle)
    next = replaceRange(next, workingBlock.bodyStart, workingBlock.bodyEnd, body)
    entryMap.set(desired.key, { key: desired.key, value, raw: '' })
    added.push(`registry entry: ${desired.key}`)
    workingBlocks = parseDefineBlocks(next).filter(block => block.kind === 'registry')
    workingBlock = workingBlocks[0]
  }

  // Meta merge — only refresh meta when structural adds happened OR known fields are missing.
  // To avoid dirty git on pure no-ops: if nothing else changed, only fill missing meta keys
  // (do not bump generatedAt alone).
  const existingMeta = parseMetaBlock(next)
  const generatedMeta = parseMetaBlock(generated)
  const structuralAdded = added.length > 0

  if (generatedMeta) {
    if (!existingMeta) {
      // Insert meta before defineRegistry export
      const blocks = parseDefineBlocks(next).filter(block => block.kind === 'registry')
      const insertAt = blocks[0]?.fullStart ?? next.length
      const metaBody = rewriteObjectBody(generatedMeta.entries, effectiveStyle)
      const metaBlock = `export const __athena_schema_meta = {${metaBody}} as const${effectiveStyle.semicolons ? ';' : ''}${effectiveStyle.newline}${effectiveStyle.newline}`
      next = replaceRange(next, insertAt, insertAt, metaBlock)
      added.push('__athena_schema_meta')
    } else {
      const desiredEntries =
        structuralAdded
          ? generatedMeta.entries
          : generatedMeta.entries.filter(entry => {
              // Only add missing keys; do not refresh generatedAt when nothing else changed
              return !existingMeta.entries.some(current => current.key === entry.key)
            })

      if (structuralAdded) {
        const merged = mergeMetaFields(existingMeta, generatedMeta)
        if (merged.changed) {
          // re-parse meta on next
          const metaNow = parseMetaBlock(next)
          if (metaNow) {
            const body = rewriteObjectBody(merged.entries, effectiveStyle)
            next = replaceRange(next, metaNow.bodyStart, metaNow.bodyEnd, body)
            added.push(...merged.added)
          }
        }
      } else if (desiredEntries.length > 0) {
        const map = new Map(existingMeta.entries.map(entry => [entry.key, entry]))
        for (const entry of desiredEntries) {
          map.set(entry.key, entry)
          added.push(`meta entry: ${entry.key}`)
        }
        const ordered = [
          ...existingMeta.entries.map(entry => map.get(entry.key)!),
          ...desiredEntries.filter(entry => !existingMeta.entries.some(e => e.key === entry.key)),
        ]
        const metaNow = parseMetaBlock(next)
        if (metaNow) {
          const body = rewriteObjectBody(ordered, effectiveStyle)
          next = replaceRange(next, metaNow.bodyStart, metaNow.bodyEnd, body)
        }
      }
    }
  }

  if (conflicts.length > 0 && added.length === 0) {
    return {
      action: 'skip',
      skipReason: 'merge-conflict',
      added: [],
      preservedCustom: preservedCustomUnits(existing, generated, 'registry'),
      conflicts,
      lintErrors: [],
      detail: conflicts.join('; '),
    }
  }

  const lintErrors = lintArtifactSource(next, 'registry')
  if (lintErrors.length > 0) {
    return {
      action: 'skip',
      skipReason: 'merge-lint-failed',
      added,
      preservedCustom: preservedCustomUnits(existing, generated, 'registry'),
      conflicts,
      lintErrors,
      detail: lintErrors.join('; '),
    }
  }

  const preservedCustom = preservedCustomUnits(next, generated, 'registry')
  if (next.replace(/\r\n/g, '\n') === existing.replace(/\r\n/g, '\n')) {
    return {
      action: 'unchanged',
      content: existing,
      skipReason: 'already-current',
      added: [],
      preservedCustom,
      conflicts,
      lintErrors: [],
    }
  }

  return {
    action: 'write',
    content: next.endsWith('\n') ? next : `${next}${effectiveStyle.newline}`,
    writeReason: 'merged',
    added,
    preservedCustom,
    conflicts,
    lintErrors: [],
  }
}

/**
 * Merge generated database/registry content into an existing on-disk file.
 * Additive only: never deletes imports or object keys.
 */
export function mergeProtectedArtifact(
  kind: Extract<GeneratorArtifactKind, 'database' | 'registry'>,
  existing: string,
  generated: string,
  policy: GeneratorArtifactWritePolicy,
): ArtifactMergeResult {
  if (policy === 'overwrite') {
    if (existing.replace(/\r\n/g, '\n') === generated.replace(/\r\n/g, '\n')) {
      return {
        action: 'unchanged',
        content: existing,
        skipReason: 'already-current',
        added: [],
        preservedCustom: [],
        conflicts: [],
        lintErrors: [],
      }
    }
    return {
      action: 'write',
      content: generated,
      writeReason: 'overwritten',
      added: ['full overwrite'],
      preservedCustom: [],
      conflicts: [],
      lintErrors: [],
    }
  }

  if (policy === 'skip') {
    return {
      action: 'skip',
      skipReason: 'protected-existing-file',
      added: [],
      preservedCustom: preservedCustomUnits(existing, generated, kind),
      conflicts: [],
      lintErrors: [],
      detail: 'artifactWrite policy is skip',
    }
  }

  return kind === 'database'
    ? mergeDatabaseArtifact(existing, generated)
    : mergeRegistryArtifact(existing, generated)
}

/**
 * Plan write action for any generated artifact (used by pipeline).
 */
export function resolveArtifactWritePlan(
  file: GeneratedArtifact,
  existingContent: string | null,
  policy: GeneratorArtifactWritePolicy | 'always',
): ArtifactMergeResult {
  if (existingContent === null) {
    return {
      action: 'write',
      content: file.content,
      writeReason: 'created',
      added: ['created'],
      preservedCustom: [],
      conflicts: [],
      lintErrors: [],
    }
  }

  if (file.kind === 'model' || file.kind === 'schema' || policy === 'always') {
    if (existingContent.replace(/\r\n/g, '\n') === file.content.replace(/\r\n/g, '\n')) {
      return {
        action: 'unchanged',
        content: existingContent,
        skipReason: 'already-current',
        added: [],
        preservedCustom: [],
        conflicts: [],
        lintErrors: [],
      }
    }
    return {
      action: 'write',
      content: file.content,
      writeReason: existingContent ? 'overwritten' : 'created',
      added: ['overwritten'],
      preservedCustom: [],
      conflicts: [],
      lintErrors: [],
    }
  }

  return mergeProtectedArtifact(
    file.kind,
    existingContent,
    file.content,
    policy,
  )
}
